import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { TypedFormBuilder, TypedFormGroup } from 'formsafe';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { RecordTypeHttpClient } from 'src/app/shared/flight-log-api/record-types/record-types-client.service';
import { ReportTypeHttpClient } from 'src/app/shared/flight-log-api/report-types/report-types-client.service';
import { TestTypeHttpClient } from 'src/app/shared/flight-log-api/test-types/test-types-client.service';
import { DrugAlcoholRecordEntity } from 'src/store/drug-alcohol-record/drug-alcohol-record.model';
import { SelectItem, toSelectionList } from 'src/util/ui/forms';
import { DrugAlcoholRecordViewModel } from 'src/view-models/drug-alcohol-record-view-model';
import { PrdProcessingState } from 'src/view-models/pilot-view-model';
import { RecordTypeViewModel } from 'src/view-models/record-type-view-model';
import { ReportTypeViewModel } from 'src/view-models/report-type-view-model';
import { TestTypeViewModel } from 'src/view-models/test-type-view-model';
import { PrdProcessingStateSelectItems } from '../pilot-form/pilot-form.component';

@Component({
  selector: 'fl-pilot-drug-alcohol-record-form',
  templateUrl: './pilot-drug-alcohol-record-form.component.html',
  styleUrls: ['./pilot-drug-alcohol-record-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PilotDrugAlcoholRecordFormComponent implements OnInit, OnDestroy {
  @Input()
  public drugAlcoholRecord?: DrugAlcoholRecordViewModel;
  @Input()
  public pilotId: string;
  public form: TypedFormGroup<DrugAlcoholRecordEntity>;
  public processingStateOptions = PrdProcessingStateSelectItems;
  public disableProcessingState = false;

  public recordTypeOptions$: Observable<SelectItem[]>;
  public reportTypeOptions$: Observable<SelectItem[]>;
  public testTypeOptions$: Observable<SelectItem[]>;

  public recordTypesearch$ = new Subject<string>();
  public reportTypesearch$ = new Subject<string>();
  public testTypesearch$ = new Subject<string>();

  public selectedRecordType: SelectItem;
  public selectedReportType: SelectItem;
  public selectedTestType: SelectItem;

  public minDate = new Date(1950, 0, 1);
  public maxDate = new Date(Date.now());

  public _onDestroy$ = new Subject();

  constructor(
    private _tfb: TypedFormBuilder,
    private _recordType: RecordTypeHttpClient,
    private _reportType: ReportTypeHttpClient,
    private _testType: TestTypeHttpClient,
    private _cf: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    const drugAlcoholRecord: Partial<DrugAlcoholRecordEntity> =
      this.drugAlcoholRecord != null ? this.drugAlcoholRecord : {};
    this.form = this._tfb.group<DrugAlcoholRecordEntity>({
      id: [drugAlcoholRecord.id],
      occurrenceDate: [drugAlcoholRecord.occurrenceDate, Validators.required],
      recordTypeId: [drugAlcoholRecord.recordTypeId, Validators.required],
      reportTypeId: [drugAlcoholRecord.reportTypeId, Validators.required],
      testTypeId: [drugAlcoholRecord.testTypeId, Validators.required],
      suppressed: [drugAlcoholRecord.suppressed ?? false],
      lastProcessedDate: [drugAlcoholRecord.lastProcessedDate],
      pilotId: [drugAlcoholRecord.pilotId ?? this.pilotId],
      prdSourceSystemId: [drugAlcoholRecord.prdSourceSystemId],
      processingError: [drugAlcoholRecord.processingError],
      processingState: [
        drugAlcoholRecord.processingState ?? PrdProcessingState.ReadyForTransmission,
      ],
    });

    if (this.drugAlcoholRecord?.recordTypeId) {
      this.setSelectedRecordType();
      this.setSelectedReportType();
      this.setSelectedTestType();
    }

    this.recordTypeOptions$ = this.observeRecordTypeOptions(this.recordTypesearch$);

    this.reportTypeOptions$ = combineLatest([
      this.reportTypesearch$,
      this.form.controls.recordTypeId.valueChanges,
    ]).pipe(
      startWith([null, null]),
      switchMap(([search, recordTypeId]) => this.observeReportTypeOptions(search, recordTypeId)),
    );

    this.testTypeOptions$ = combineLatest([
      this.testTypesearch$,
      this.form.controls.reportTypeId.valueChanges,
    ]).pipe(
      startWith([null, null]),
      switchMap(([search, reportTypeId]) => this.observeTestTypeOptions(search, reportTypeId)),
    );

    this.form.controls.recordTypeId.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      if (this.form.touched) {
        this.form.controls.reportTypeId.reset();
        this.form.controls.testTypeId.reset();
      }
    });

    this.form.controls.reportTypeId.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((v) => {
        if (this.form.touched) {
          this.form.controls.testTypeId.reset();
        }
      });
  }

  public onRecordTypeSearch(searchTerm: string) {
    return this.recordTypesearch$.next(searchTerm);
  }

  public onReportTypeSearch(searchTerm: string) {
    return this.reportTypesearch$.next(searchTerm);
  }

  public onTestTypeSearch(searchTerm: string) {
    return this.testTypesearch$.next(searchTerm);
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public getControlRequiredError(control: any) {
    if (control.valid) {
      return [];
    } else {
      const errors: string[] = [];
      if (control.hasError('required')) {
        errors.push('Reason is required');
      }
      return errors;
    }
  }

  public async setSelectedRecordType() {
    const recordType = await this.drugAlcoholRecord?.recordType$.pipe(map((rt) => rt)).toPromise();
    if (recordType) {
      this.selectedRecordType = {
        value: recordType.id,
        label: recordType.displayName(),
      };
    }
    this._cf.markForCheck();
  }

  public async setSelectedReportType() {
    const reportType = await this.drugAlcoholRecord?.reportType$.pipe(map((rt) => rt)).toPromise();
    if (reportType) {
      this.selectedReportType = {
        value: reportType.id,
        label: reportType.displayName(),
      };
    }
    this._cf.markForCheck();
  }

  public async setSelectedTestType() {
    const testType = await this.drugAlcoholRecord?.testType$.pipe(map((tt) => tt)).toPromise();
    if (testType) {
      this.selectedTestType = {
        value: testType.id,
        label: testType.displayName(),
      };
    }
    this._cf.markForCheck();
  }

  public observeRecordTypeOptions(searchTerm: Observable<string | null>) {
    return searchTerm
      .pipe(startWith(null as string | null))
      .pipe(
        switchMap((search) =>
          this._recordType.getAll({
            searchTerm: search ?? undefined,
            take: 25,
          }),
        ),
      )
      .pipe(map((r) => r.results?.sort((a, b) => a.description.localeCompare(b.description))))
      .pipe(
        map(
          toSelectionList<RecordTypeViewModel>((recordType) => `${recordType.displayName()}`, 'id'),
        ),
      );
  }

  public observeReportTypeOptions(
    searchTerm: string | null,
    recordTypeId: string | null | undefined,
  ) {
    const reportTypeOptions = recordTypeId
      ? this._reportType.getByRecordTypeId(recordTypeId).pipe(
          map((data) => data.results?.sort((a, b) => a.description.localeCompare(b.description))),
          map((reportTypes) => {
            if (searchTerm) {
              return reportTypes.filter(
                (reportType) =>
                  reportType.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  reportType.description.toLowerCase().includes(searchTerm.toLowerCase()),
              );
            } else {
              return reportTypes;
            }
          }),
        )
      : of(([] as any) as ReportTypeViewModel[]);
    return reportTypeOptions.pipe(
      map(
        toSelectionList<ReportTypeViewModel>((reportType) => `${reportType.displayName()}`, 'id'),
      ),
    );
  }

  public observeTestTypeOptions(
    searchTerm: string | null,
    reportTypeId: string | null | undefined,
  ) {
    const testTypeOptions = reportTypeId
      ? this._testType.getByReportTypeId(reportTypeId).pipe(
          map((data) => data.results?.sort((a, b) => a.description.localeCompare(b.description))),
          map((testTypes) => {
            if (searchTerm) {
              return testTypes.filter(
                (testType) =>
                  testType.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  testType.description.toLowerCase().includes(searchTerm.toLowerCase()),
              );
            } else {
              return testTypes;
            }
          }),
        )
      : of(([] as any) as TestTypeViewModel[]);
    return testTypeOptions.pipe(
      map(toSelectionList<TestTypeViewModel>((testType) => `${testType.displayName()}`, 'id')),
    );
  }
}
